import React, { ReactNode } from 'react';
import Div from '../Element/Div/Div';
import styles from './Step.module.scss';
import BodyText from '../Typography/BodyText/BodyText';
import Icon from '../Icon/Icon';
import Flex from '../Flex/Flex';
import { ResponsiveHiddenProp } from '../../components/Element/Element';

type StepProps = {
  /** String or number that appears as the label inside the step indicator */
  label: ReactNode | string | number;
  /** String or component that appears next to the step indicator */
  description?: ReactNode | string;
  /** Control responsiveHidden prop of description container */
  descriptionResponsiveHidden?: ResponsiveHiddenProp;
  /** Pile description and step icon vertically */
  stepContentToColumn?: boolean;
  /** Boolean that determines whether the indicator is in an active state or not */
  active?: boolean;
  /** Boolean that determines whether the indicator is in completed state or not */
  completed?: boolean;
  /** Boolean that determines whether the indicator can be selected (clicked) or not */
  selectable?: boolean;
  /** Click handler; disabled if the component is not marked "selectable" */
  onSelect?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  /** An element (e.g. Icon) that appears inside a "completed" state indicator - true will use the default icon */
  completionIcon?: boolean | React.ReactNode;
  /** Boolean that triggers an always-bright variant for completed state items */
  alwaysBrightCompleted?: boolean;
};
// Alias for backwards compatibility
export type StepAttributes = StepProps;

const Step: React.FunctionComponent<StepProps> = ({
  label,
  description,
  descriptionResponsiveHidden = { thinnerThan: 'sm' },
  stepContentToColumn = false,
  active,
  completed,
  selectable,
  onSelect,
  completionIcon,
  alwaysBrightCompleted,
}) => {
  const descriptionElement =
    typeof description === 'string' ? (
      <BodyText tagName="span" size="sm" color="gray400">
        {description}
      </BodyText>
    ) : (
      description
    );

  const labelClasses = [
    styles.step_label,
    {
      [styles.step_label__active]: active,
      [styles.step_label__selectable]: selectable,
      [styles.step_label__alwaysBrightCompleted]:
        alwaysBrightCompleted && completed,
      [styles.step_label__withIcon]: completionIcon,
    },
  ];

  const useCompletionIcon = completed && completionIcon;

  completionIcon = React.isValidElement(completionIcon) ? (
    completionIcon
  ) : (
    <Icon className={labelClasses} name="checkmark" size="sm" />
  );

  const tempLabel =
    typeof label === 'string' || typeof label === 'number' ? (
      <BodyText tagName="span" size="sm" className={labelClasses}>
        {String(label)}
      </BodyText>
    ) : (
      <Div className={labelClasses}>{label}</Div>
    );
  const labelElement = useCompletionIcon ? completionIcon : tempLabel;

  const stepClasses = [
    styles.step,
    {
      [styles.step__selectable]: selectable,
      [styles.step__contentInColumn]: stepContentToColumn,
    },
  ];

  const borderClasses = [
    styles.step_border,
    {
      [styles.step_border__active]: active,
      [styles.step_border__selectable]: selectable,
      [styles.step_border__alwaysBrightCompleted]:
        alwaysBrightCompleted && completed,
    },
  ];

  const clickHandler = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    selectable && onSelect && onSelect(event);
  };

  return (
    <Flex centeredVertically className={stepClasses} onClick={clickHandler}>
      <Div className={borderClasses}>{labelElement}</Div>
      {descriptionElement && (
        <Div
          padding={{ horizontal: 'xs' }}
          responsiveHidden={descriptionResponsiveHidden}
        >
          {descriptionElement}
        </Div>
      )}
    </Flex>
  );
};

export default Step;
